import { Extension, type Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

import { isSystemTr } from './shared';

export const DOCUMENT_CONTENT_REPLACED_META = 'documentContentReplaced';

const explicitContentSelectionKey = new PluginKey<boolean>('explicitContentSelection');
const USER_INTERACTION_META = 'userInteraction';

/**
 * ProseMirror always has a selection. When loaded content starts with an atom, its default
 * selection is a non-empty NodeSelection even though the user did not select anything. Track
 * whether a selection was reached through an interaction so consumers can ignore that default.
 */
const ExplicitContentSelection = Extension.create({
  name: 'explicitContentSelection',

  addProseMirrorPlugins() {
    return [
      new Plugin<boolean>({
        key: explicitContentSelectionKey,
        state: {
          init: () => false,
          apply(transaction, wasExplicit) {
            if (transaction.getMeta(DOCUMENT_CONTENT_REPLACED_META)) return false;
            if (transaction.getMeta(explicitContentSelectionKey) === USER_INTERACTION_META) return true;
            if (transaction.selectionSet && !isSystemTr(transaction)) return true;
            return wasExplicit;
          },
        },
        props: {
          handleDOMEvents: {
            pointerdown(view) {
              // Ordinary text selection will set transaction.selectionSet itself. Dispatch metadata
              // only for the ambiguous case where the initial automatic node selection may stay equal
              // after the user deliberately clicks that same atom.
              if (!view.state.selection.empty && !explicitContentSelectionKey.getState(view.state)) {
                view.dispatch(view.state.tr.setMeta(explicitContentSelectionKey, USER_INTERACTION_META));
              }
              return false;
            },
          },
        },
      }),
    ];
  },
});

export const hasExplicitContentSelection = (editor: Editor): boolean => {
  if (editor.state.selection.empty) return false;
  const wasExplicit = explicitContentSelectionKey.getState(editor.state);
  // Editors outside the document runtime do not install this extension. Preserve their previous
  // selection behavior instead of treating every non-empty selection as absent.
  return wasExplicit ?? true;
};

export default ExplicitContentSelection;
